import { AiFormDefinition, AiFormField, AiFormState } from './ai-form.types';

export function isAiFormPage(form: AiFormDefinition): boolean {
    if (form.matchesPage) return form.matchesPage();
    const target = new URL(form.createPath, window.location.origin);
    return target.pathname === window.location.pathname && target.hash === window.location.hash;
}

export function readAiFormValues(
    fields: AiFormField[], values: Record<string, any>, state: AiFormState,
): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const field of fields) {
        let value = values[field.modelKey ?? field.key];
        if (field.processing?.read) value = field.processing.read(value, state);
        result[field.key] = field.type === 'array'
            ? (value ?? []).map(item => readAiFormValues(field.items!, item, state))
            : value ?? null;
    }
    return result;
}

export async function writeAiFormValues(
    fields: AiFormField[], values: Record<string, unknown>, state: AiFormState,
    currentValues: Record<string, any> = state.values, complete = false, signal?: AbortSignal,
): Promise<Record<string, unknown>> {
    const result: Record<string, unknown> = {};
    for (const field of fields) {
        if (field.readOnly) continue;
        if (!complete && !Object.prototype.hasOwnProperty.call(values, field.key)) continue;
        const modelKey = field.modelKey ?? field.key;
        const currentValue = currentValues[modelKey];
        let value = values[field.key] ?? (field.type === 'array' ? [] : null);
        if (field.type === 'array') {
            // По умолчанию массив заменяется целиком; updateMode=patch сохраняет пропущенные поля строк.
            value = await Promise.all((value as Record<string, unknown>[]).map((item, index) =>
                writeAiFormValues(field.items!, item, state, currentValue?.[index] ?? {}, field.updateMode !== 'patch', signal)));
        }
        result[modelKey] = field.processing?.write
            ? await field.processing.write(value, state, currentValue, { field, signal }) : value;
    }
    return result;
}

export function resolveAiFormLookupField(fields: AiFormField[], path: string): { field: AiFormField; path: string } {
    const collect = (items: AiFormField[], prefix = ''): { field: AiFormField; path: string }[] => items.flatMap(field => {
        const fullPath = prefix + field.key;
        return [...(field.caller ? [{ field, path: fullPath }] : []), ...(field.items ? collect(field.items, fullPath + '.') : [])];
    });
    const options = collect(fields);
    const exact = options.find(item => item.path === path);
    if (exact) return exact;
    const matches = options.filter(item => item.field.key === path);
    if (matches.length === 1) return matches[0];
    throw new Error(`${matches.length ? 'Неоднозначное' : 'Неизвестное'} поле поиска «${path}». Используйте lookupPath из схемы: `
        + (matches.length ? matches : options).map(item => item.path).join(', '));
}

export function describeAiFormFields(fields: AiFormField[], prefix = ''): AiFormField[] {
    return fields.map(({ processing, caller, modelKey, items, ...field }) => ({
        ...field, ...(caller ? { lookup: true, lookupPath: prefix + field.key } : {}),
        ...(items ? { items: describeAiFormFields(items, prefix + field.key + '.') } : {}),
    }));
}
